/**
 * BILLING currency — the currency a deployment actually charges in.
 *
 * Distinct from the DISPLAY currency in `budget.ts`, and the two must not be
 * confused. Display currency is a UI multiplier applied to USD-metered LLM
 * spend at an admin-set constant (`tenant_settings.display_currency_rate`);
 * its own doc comment warns it must NEVER be used as an FX rate for real
 * charging. This module is the other side: amounts that a payment provider
 * will really move, stored in the currency's own minor units and never
 * converted at read time.
 *
 * WHY IT EXISTS: Papan is deployed whitelabel, and the original billing code
 * hardcoded `'myr'` in the two Stripe catalog-sync modules, plus `en-MY`/`MYR`
 * and a literal `RM` in the UI. A client deploying their own instance sells in
 * their own currency, so the currency has to travel with the priced row
 * (`billing_plans.currency`, `usage_credit_packs.currency`) rather than being
 * a constant in our source.
 *
 * ── The minor-unit rule ──────────────────────────────────────────────
 * Every price column stores MINOR UNITS (sen, cents, yen, fils). That is also
 * exactly what Stripe's `unit_amount` takes, for every currency — so the
 * provider path is a straight pass-through and needs no conversion at all.
 * The exponent matters in two places only:
 *
 *   1. DISPLAY — dividing by 100 is wrong for JPY (0 decimals) and for BHD
 *      (3 decimals). `formatCurrencyMinor` divides by 10^exponent.
 *   2. INPUT — an admin typing "1000" for a JPY plan means ¥1000, not ¥10.
 *      `parseMajorToMinor` multiplies by 10^exponent.
 *
 * Getting this backwards is a 100× billing error in either direction, which
 * is why both directions live here rather than being open-coded per call site.
 */

/**
 * Currencies this deployment's billing layer will accept.
 *
 * A deliberately short allowlist rather than "any ISO-4217 code": the value
 * flows straight into `Intl.NumberFormat` (throws on garbage, at render time
 * in a customer's browser) and into Stripe (which rejects codes the connected
 * account isn't enabled for, at charge time). Both failures land far from the
 * admin who typed it, so it is validated at write time instead.
 *
 * Includes MYR (our own deployment), the majors, and the regional currencies a
 * SEA/APAC or Gulf client is most likely to want. Two entries are here for
 * correctness rather than demand, and both must stay: JPY/KRW/VND exercise the
 * ZERO-decimal path and BHD/KWD/OMR/JOD/TND the THREE-decimal one. Without a
 * member of each class in the allowlist, the exponent handling below is
 * unreachable through the admin routes and silently rots.
 *
 * Extending it is a one-line change plus a check that Stripe supports the code
 * for the target account.
 */
export const SUPPORTED_BILLING_CURRENCIES = [
  'MYR', 'USD', 'EUR', 'GBP', 'SGD', 'AUD', 'NZD', 'CAD', 'CHF',
  'CNY', 'HKD', 'TWD', 'INR', 'IDR', 'THB', 'PHP',
  // Zero-decimal: `unit_amount` is whole units, no ×100 anywhere.
  'JPY', 'KRW', 'VND',
  // Gulf. AED/SAR/QAR are 2-decimal; the rest are THREE-decimal and Stripe
  // additionally requires their amounts to be a multiple of 10.
  'AED', 'SAR', 'QAR', 'BHD', 'KWD', 'OMR', 'JOD', 'TND',
] as const

export type BillingCurrency = typeof SUPPORTED_BILLING_CURRENCIES[number]

export function isSupportedBillingCurrency(v: unknown): v is BillingCurrency {
  return typeof v === 'string' && (SUPPORTED_BILLING_CURRENCIES as readonly string[]).includes(v.toUpperCase())
}

/**
 * Currencies Stripe treats as ZERO-DECIMAL: `unit_amount` is whole units, so
 * `unit_amount: 1000` on a JPY price is ¥1,000 — not ¥10.
 *
 * This matches our storage (minor units, exponent 0 ⇒ minor == major), so the
 * pass-through stays correct. The set is kept explicit anyway because it is
 * what `assertStripeMinorAmount` uses to sanity-check an amount before it
 * becomes a real charge, and because Intl and Stripe have historically
 * disagreed on a few codes (Stripe's list is the authority for the API call;
 * Intl's is the authority for display).
 *
 * Source: Stripe "Zero-decimal currencies". Re-check when adding a currency.
 */
export const STRIPE_ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
])

/**
 * Currencies Stripe bills in thousandths but requires to be a MULTIPLE OF 10 —
 * i.e. the API takes 3-decimal minor units, then insists the last digit is 0.
 * An amount like 1_234 fils is rejected at charge time, not at save time,
 * which is why the catalog sync asserts it up front.
 *
 * Source: Stripe "Three-decimal currencies".
 */
export const STRIPE_THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BHD', 'JOD', 'KWD', 'OMR', 'TND',
])

/**
 * How many minor units make one major unit, as a power of 10.
 *
 * Resolved from `Intl` (which implements ISO-4217 minor units) so we don't
 * ship and maintain a 180-row table, with the Stripe zero-decimal set applied
 * on top — because what we store must agree with what Stripe charges, and
 * Stripe is the one moving the money. Falls back to 2 if `Intl` can't resolve
 * the code, which is the right guess for an unknown currency and matches every
 * pre-existing amount in the database.
 */
export function currencyMinorUnitExponent(currency: string): number {
  const code = currency.toUpperCase()
  if (STRIPE_ZERO_DECIMAL_CURRENCIES.has(code)) return 0
  if (STRIPE_THREE_DECIMAL_CURRENCIES.has(code)) return 3
  try {
    const digits = new Intl.NumberFormat('en-US', { style: 'currency', currency: code })
      .resolvedOptions().maximumFractionDigits
    if (typeof digits !== 'number') return 2
    // Node's ICU/CLDR disagrees with Stripe on a few codes — the two most
    // commonly cited are IDR (reported as 0-decimal by newer ICU builds
    // reflecting Bank Indonesia's rounding rules; Stripe still bills 2-decimal
    // per ISO 4217) and TWD (similar). The disagreement is version-dependent:
    // macOS Node reports 2 for IDR, GitHub Actions Linux reports 0, so the
    // same code silently produced two different `minorUnitsPerMajor` values —
    // which is a 100× charge error against whichever side you were NOT on.
    // Stripe is the authority (they move the money) and is not on our zero-
    // decimal set, so an Intl-reported 0 for a code Stripe treats as ≥2 is
    // wrong. Snap up to 2 in that case; a real zero-decimal currency is in
    // STRIPE_ZERO_DECIMAL_CURRENCIES and returned above.
    if (digits === 0) return 2
    return digits
  } catch {
    return 2
  }
}

/** 10^exponent — the number of minor units in one major unit. */
export function currencyMinorUnitsPerMajor(currency: string): number {
  return 10 ** currencyMinorUnitExponent(currency)
}

/**
 * Minor units → a localized currency string ("RM 49.00", "¥1,000", "$29.00").
 *
 * The replacement for the old `formatMyr`, which hardcoded `en-MY`/`MYR` and
 * an unconditional `/100`. Locale defaults to `undefined` so the runtime picks
 * the viewer's, which is the right call for a whitelabel product: the same
 * MYR price should read "RM 49.00" to a Malaysian admin and "MYR 49.00" to a
 * German one, without us mapping currencies to locales.
 */
export function formatCurrencyMinor(
  minorUnits: number,
  currency: string,
  locale?: string,
): string {
  const exponent = currencyMinorUnitExponent(currency)
  const amount = minorUnits / 10 ** exponent
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(amount)
  } catch {
    // MALFORMED code (not three letters) — `Intl` accepts any well-formed
    // 3-letter code and simply renders it as its own label, so this branch is
    // narrower than it looks. Degrade to a readable, labeled amount rather
    // than throwing inside a render — same policy as formatMoney.
    return `${amount.toFixed(exponent)} ${currency.toUpperCase()}`
  }
}

/**
 * The currency's symbol ("RM", "$", "¥"), for use as an input adornment where
 * a full formatted amount would be wrong.
 *
 * Extracted by formatting zero and stripping the digits, because `Intl` has no
 * "give me just the symbol" API. Falls back to the uppercase ISO code, which
 * is a correct label in its own right — better than an empty adornment when a
 * runtime's ICU data has no symbol for the code.
 */
export function currencySymbol(currency: string, locale?: string): string {
  const code = currency.toUpperCase()
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0)
    const symbol = parts.find((p) => p.type === 'currency')?.value
    return symbol || code
  } catch {
    return code
  }
}

/**
 * A typed major-unit amount ("100", "99.50", "1,000") → minor units, or null
 * if it isn't a usable amount.
 *
 * The currency-aware replacement for `parseMyrToCents`. Same deliberate
 * strictness, and for the same reason: it is not the authority on bounds (the
 * server re-validates floor/ceiling — a client check can be skipped entirely),
 * but it rules out the input class that would make a bounds check meaningless.
 * NaN, negatives, and sub-minor-unit precision are rejected rather than
 * coerced, so "10O" is an error and not 10, and "9.999" on a 2-decimal
 * currency is an error rather than silently becoming a different charge than
 * the one displayed.
 *
 * Grouping separators are stripped (people paste "1,000"). The accepted
 * decimal count follows the currency: none for JPY, three for BHD.
 */
export function parseMajorToMinor(input: string, currency: string): number | null {
  const exponent = currencyMinorUnitExponent(currency)
  const cleaned = input.trim().replace(/,/g, '')
  const pattern = exponent === 0
    ? /^\d+$/
    : new RegExp(`^\\d+(\\.\\d{1,${exponent}})?$`)
  if (!pattern.test(cleaned)) return null
  const minor = Math.round(Number(cleaned) * 10 ** exponent)
  return Number.isFinite(minor) && minor > 0 ? minor : null
}

/**
 * Fail loudly if a minor-unit amount can't legally be charged in `currency`.
 *
 * Called by the catalog sync before a Price is minted, so a bad amount is a
 * refused sync rather than a Stripe rejection at a customer's checkout — or,
 * worse, a silently-accepted charge off by a factor of 10.
 *
 * Returns an error string (never throws) so the caller can surface it as a
 * validation message on the admin form; `null` means the amount is fine.
 */
export function assertStripeMinorAmount(minorUnits: number, currency: string): string | null {
  const code = currency.toUpperCase()
  if (!Number.isInteger(minorUnits)) {
    return `amount must be a whole number of minor units (got ${minorUnits})`
  }
  if (minorUnits < 0) return 'amount must not be negative'
  if (STRIPE_THREE_DECIMAL_CURRENCIES.has(code) && minorUnits % 10 !== 0) {
    return `${code} is a three-decimal currency: Stripe requires the amount to be a multiple of 10 (got ${minorUnits})`
  }
  return null
}
